import {
  createLedgerApi,
  deleteLedgerApi,
  getLedgerApi,
  getLedgersApi,
  revertLedgerApi,
  updateLedgerApi,
} from '@app/api/ledger.api';
import { LedgerDataArray, LedgerItem, LedgerQueryParams, LedgerModel } from '@app/interfaces/ledger';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface LedgerState {
  data: LedgerModel[];
  totalRows: number;
  pagination: {
    current: number;
    pageSize: number;
  };
  filters: any;
  sorter: any;
  searchTerm: string;
  selectedRecord: LedgerItem[];
  loading: boolean;
  error: string | null;
  record: LedgerModel | null;
}

const initialState: LedgerState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: '',
  selectedRecord: [],
  loading: false,
  error: null,
  record: null,
};

interface ResponseData {
  data: LedgerItem;
  message: string;
}

export const getLedgers = createAsyncThunk<LedgerDataArray, LedgerQueryParams>(
  'ledger/getLedgers',
  async (params: LedgerQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getLedgersApi(params)) as LedgerDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getLedger = createAsyncThunk<LedgerModel, number>('ledger/getLedger', (async (
  ledgerId: number,
  { rejectWithValue }: any,
) => {
  try {
    const data = await getLedgerApi(ledgerId);
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
}) as any);

export const createLedger = createAsyncThunk<ResponseData, LedgerModel>('ledger/createLedger', async (data) => {
  const response = await createLedgerApi(data);
  return response;
});

export const updateLedger = createAsyncThunk<ResponseData, { ledgerId: number; formData: LedgerModel }>(
  'ledger/updateLedger',
  async ({ ledgerId, formData }) => {
    const response = await updateLedgerApi(ledgerId, formData);
    return response;
  },
);

export const deleteLedger = createAsyncThunk<{ ledgerId: number; response: any }, number>(
  'ledger/deleteLedger',
  async (ledgerId, { rejectWithValue }) => {
    try {
      const response = await deleteLedgerApi(ledgerId);
      return { ledgerId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const revertLedger = createAsyncThunk<{ ledgerId: number; response: any }, number>(
  'ledger/revertLedger',
  async (ledgerId, { rejectWithValue }) => {
    try {
      const response = await revertLedgerApi(ledgerId);
      return { ledgerId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const ledgerSlice = createSlice({
  name: 'ledger',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      if (pagination !== undefined) state.pagination = pagination;
      if (filters !== undefined) state.filters = filters;
      if (sorter !== undefined) state.sorter = sorter;
      if (searchTerm !== undefined) state.searchTerm = searchTerm;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLedgers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getLedgers.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: LedgerItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
      })
      .addCase(getLedgers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch products';
      })
      .addCase(getLedger.fulfilled, (state, action) => {
        const ledger = action.payload;
        state.record = ledger;
      })
      .addCase(createLedger.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateLedger.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => Number(item.id) === Number(data.id));
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          state.record = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteLedger.fulfilled, (state, action) => {
        const { ledgerId, response } = action.payload;
        state.data = state.data.filter((ledger) => Number(ledger.id) !== ledgerId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = ledgerSlice.actions;

export default ledgerSlice.reducer;
